import React, { useEffect, useState } from 'react';
import { Save, Globe, Layers } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import AdminCard from '../../components/admin/AdminCard';
import { seoAdminAPI, programsAdminAPI } from '../../services/api';
import '../../styles/admin-shared.css';

const TABS = [
  { id: 'global',   label: 'Global',   icon: <Globe  size={16} /> },
  { id: 'programs', label: 'Programas', icon: <Layers size={16} /> },
];

const EMPTY_SETTINGS = {
  site_title: '', site_description: '', default_keywords: '', canonical_base_url: '',
  default_og_image: '', default_twitter_handle: '',
  google_verification: '', bing_verification: '', facebook_verification: '',
  google_analytics_id: '', google_tag_manager_id: '', meta_pixel_id: '',
  robots_txt: '', sitemap_enabled: true,
  organization_name: '', organization_logo: '', organization_sameas: [],
};

const EMPTY_PROGRAM_SEO = {
  seo_title: '', seo_description: '', keywords: '',
  canonical_url: '', og_image: '', schema_type: 'SoftwareApplication',
};

const SeoSettingsPage = () => {
  const [tab, setTab] = useState('global');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [settings, setSettings] = useState(EMPTY_SETTINGS);
  const [programs, setPrograms] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState('');
  const [programSeo, setProgramSeo] = useState(EMPTY_PROGRAM_SEO);

  useEffect(() => {
    (async () => {
      try {
        const [s, p] = await Promise.all([
          seoAdminAPI.getSettings(),
          programsAdminAPI.list(),
        ]);
        const merged = { ...EMPTY_SETTINGS, ...(s.data.data || {}) };
        if (!Array.isArray(merged.organization_sameas)) merged.organization_sameas = [];
        setSettings(merged);
        const list = p.data.data || [];
        setPrograms(list);
        if (list[0]) setSelectedProgram(list[0].id);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Carrega SEO do programa selecionado
  useEffect(() => {
    if (!selectedProgram) return;
    (async () => {
      try {
        const { data } = await seoAdminAPI.getProgramSeo(selectedProgram);
        setProgramSeo({ ...EMPTY_PROGRAM_SEO, ...(data.data || {}) });
      } catch (err) {
        console.error(err);
        setProgramSeo(EMPTY_PROGRAM_SEO);
      }
    })();
  }, [selectedProgram]);

  const setS = (k) => (e) => setSettings({ ...settings, [k]: e.target?.value ?? e });
  const setPS = (k) => (e) => setProgramSeo({ ...programSeo, [k]: e.target?.value ?? e });

  const saveGlobal = async () => {
    setSaving(true);
    try {
      const payload = { ...settings };
      if (typeof payload.organization_sameas === 'string') {
        payload.organization_sameas = payload.organization_sameas.split('\n').map((s) => s.trim()).filter(Boolean);
      }
      await seoAdminAPI.updateSettings(payload);
      alert('Configurações salvas!');
    } catch (err) {
      alert(err.response?.data?.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const saveProgram = async () => {
    if (!selectedProgram) return;
    setSaving(true);
    try {
      await seoAdminAPI.upsertProgramSeo(selectedProgram, programSeo);
      alert('SEO do programa salvo!');
    } catch (err) {
      alert(err.response?.data?.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <AdminLayout><div>Carregando…</div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="admin-page">
        <header className="admin-page-header">
          <h1>SEO</h1>
          <p>Configurações globais e por programa.</p>
        </header>

        <div className="admin-tabs" style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          {TABS.map((t) => (
            <button key={t.id} className={`admin-tab ${tab === t.id ? 'active' : ''}`} onClick={() => setTab(t.id)}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {tab === 'global' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <AdminCard>
              <h3 style={{ marginTop: 0 }}>Identidade</h3>
              <div className="form-row"><label>Título do site</label>
                <input value={settings.site_title} onChange={setS('site_title')} /></div>
              <div className="form-row"><label>Descrição padrão</label>
                <textarea rows={2} value={settings.site_description} onChange={setS('site_description')} /></div>
              <div className="form-row"><label>Keywords padrão</label>
                <input value={settings.default_keywords || ''} onChange={setS('default_keywords')} /></div>
              <div className="form-row"><label>URL canônica base</label>
                <input value={settings.canonical_base_url} onChange={setS('canonical_base_url')} placeholder="https://lifyup.com.br" /></div>
              <div className="form-row"><label>OG image padrão</label>
                <input value={settings.default_og_image || ''} onChange={setS('default_og_image')} /></div>
              <div className="form-row"><label>Twitter handle padrão</label>
                <input value={settings.default_twitter_handle || ''} onChange={setS('default_twitter_handle')} placeholder="@lifeup" /></div>
            </AdminCard>

            <AdminCard>
              <h3 style={{ marginTop: 0 }}>Verificações de propriedade</h3>
              <div className="form-row"><label>Google</label>
                <input value={settings.google_verification || ''} onChange={setS('google_verification')} /></div>
              <div className="form-row"><label>Bing</label>
                <input value={settings.bing_verification || ''} onChange={setS('bing_verification')} /></div>
              <div className="form-row"><label>Facebook</label>
                <input value={settings.facebook_verification || ''} onChange={setS('facebook_verification')} /></div>
            </AdminCard>

            <AdminCard>
              <h3 style={{ marginTop: 0 }}>Tracking</h3>
              <div className="form-row"><label>Google Analytics ID</label>
                <input value={settings.google_analytics_id || ''} onChange={setS('google_analytics_id')} placeholder="G-XXXX" /></div>
              <div className="form-row"><label>Google Tag Manager ID</label>
                <input value={settings.google_tag_manager_id || ''} onChange={setS('google_tag_manager_id')} placeholder="GTM-XXXX" /></div>
              <div className="form-row"><label>Meta Pixel ID</label>
                <input value={settings.meta_pixel_id || ''} onChange={setS('meta_pixel_id')} /></div>
            </AdminCard>

            <AdminCard>
              <h3 style={{ marginTop: 0 }}>Robots / Sitemap</h3>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={settings.sitemap_enabled !== false}
                  onChange={(e) => setSettings({ ...settings, sitemap_enabled: e.target.checked })} />
                Sitemap habilitado
              </label>
              <div className="form-row" style={{ marginTop: 12 }}>
                <label>robots.txt (deixe em branco para usar padrão)</label>
                <textarea rows={6} value={settings.robots_txt || ''} onChange={setS('robots_txt')}
                  style={{ fontFamily: 'ui-monospace, monospace' }} placeholder="User-agent: *\nAllow: /\nDisallow: /admin" />
              </div>
            </AdminCard>

            <AdminCard>
              <h3 style={{ marginTop: 0 }}>Schema.org — Organização</h3>
              <div className="form-row"><label>Nome da organização</label>
                <input value={settings.organization_name || ''} onChange={setS('organization_name')} /></div>
              <div className="form-row"><label>Logo URL</label>
                <input value={settings.organization_logo || ''} onChange={setS('organization_logo')} /></div>
              <div className="form-row"><label>sameAs (1 URL por linha — redes sociais)</label>
                <textarea rows={4} value={Array.isArray(settings.organization_sameas) ? settings.organization_sameas.join('\n') : (settings.organization_sameas || '')}
                  onChange={(e) => setSettings({ ...settings, organization_sameas: e.target.value })} /></div>
            </AdminCard>

            <button className="btn-primary" onClick={saveGlobal} disabled={saving}>
              <Save size={16} /> {saving ? 'Salvando…' : 'Salvar configurações globais'}
            </button>
          </div>
        )}

        {tab === 'programs' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <AdminCard>
              <div className="form-row">
                <label>Programa</label>
                <select value={selectedProgram} onChange={(e) => setSelectedProgram(e.target.value)}>
                  {programs.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                </select>
              </div>
            </AdminCard>

            {selectedProgram && (
              <>
                <AdminCard>
                  <h3 style={{ marginTop: 0 }}>SEO do programa</h3>
                  <div className="form-row"><label>SEO Title</label>
                    <input value={programSeo.seo_title || ''} onChange={setPS('seo_title')} /></div>
                  <div className="form-row"><label>SEO Description</label>
                    <textarea rows={3} value={programSeo.seo_description || ''} onChange={setPS('seo_description')} /></div>
                  <div className="form-row"><label>Keywords</label>
                    <input value={programSeo.keywords || ''} onChange={setPS('keywords')} /></div>
                  <div className="form-row"><label>Canonical URL</label>
                    <input value={programSeo.canonical_url || ''} onChange={setPS('canonical_url')} /></div>
                  <div className="form-row"><label>OG image</label>
                    <input value={programSeo.og_image || ''} onChange={setPS('og_image')} /></div>
                  <div className="form-row"><label>Schema.org type</label>
                    <select value={programSeo.schema_type || 'SoftwareApplication'} onChange={setPS('schema_type')}>
                      <option value="SoftwareApplication">SoftwareApplication</option>
                      <option value="MobileApplication">MobileApplication</option>
                      <option value="WebApplication">WebApplication</option>
                      <option value="Product">Product</option>
                    </select>
                  </div>
                </AdminCard>

                <button className="btn-primary" onClick={saveProgram} disabled={saving}>
                  <Save size={16} /> {saving ? 'Salvando…' : 'Salvar SEO do programa'}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default SeoSettingsPage;
